import { GoogleMap, Marker, StandaloneSearchBox, useJsApiLoader } from '@react-google-maps/api'
import React, { useEffect, useRef, useState } from 'react'

const hideSpinnerStyles = `
  .number-input-no-spinner::-webkit-outer-spin-button,
  .number-input-no-spinner::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .number-input-no-spinner[type=number] {
    -moz-appearance: textfield;
  }
`

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style')
  styleSheet.textContent = hideSpinnerStyles
  document.head.appendChild(styleSheet)
}

const libraries = ['places', 'geometry'] as any[]

interface BTSMRTStation {
  id: number
  name: string
  type: string
  line_name: string
}

interface NearbyBTSMRT {
  id: string
  stationId: string
  stationName: string
  walkingMinutes: string
}

interface NearbyPopularLocation {
  id: string
  locationName: string
  walkingMinutes: string
}

interface PropertyFormData {
  propertyType: string
  unitSize: string
  bedrooms: string
  bathrooms: string
  floorNumber: string
  roomNumber: string
  roomPosition: string
  hasBalcony: boolean | null
  isPetFriendly: boolean | null
  maxGuests: string
  availability: string
  availableDate: any
  propertyPhotos: string[]
  videoTour: string | null
  floorPlan: string | null
  monthlyPrice: string
  sixMonthPrice: string
  yearlyPrice: string
  securityDeposit: string
  useHaupAgent: boolean
  listingTitle: string
  shortDescription: string
  uniqueSellingPoints: string[]
  nearbyHighlights: string[]
  noiseLevel: string
  viewType: string[]
  amenities: string[]
  buildingFacilities: string[]
  locationName: string
  location: string
  locationLat: number | null
  locationLng: number | null
  nearbyBTSMRT: NearbyBTSMRT[]
  nearbyPopularLocations: NearbyPopularLocation[]
}

interface Step1_1Props {
  onNext: () => void
  onPrevious: () => void
  onStepProgress: (progress: number) => void
  formData: PropertyFormData
  updateFormData: (data: Partial<PropertyFormData>) => void
}

const Step1_1: React.FC<Step1_1Props> = ({
  onNext,
  onPrevious,
  onStepProgress,
  formData,
  updateFormData,
}) => {
  const [progress, setProgress] = useState(0)
  const [showBTSMRTModal, setShowBTSMRTModal] = useState(false)
  const [selectedBTSMRTIndex, setSelectedBTSMRTIndex] = useState<number | null>(null)
  const [btsmrtStations, setBtsmrtStations] = useState<BTSMRTStation[]>([])
  const [stationsLoading, setStationsLoading] = useState(false)
  const [stationsError, setStationsError] = useState<string | null>(null)
  const [stationsGrouped, setStationsGrouped] = useState<{ [key: string]: BTSMRTStation[] }>({})
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedLineFilter, setSelectedLineFilter] = useState<string>('All')
  const [selectedStations, setSelectedStations] = useState<BTSMRTStation[]>([])
  const [uniqueLines, setUniqueLines] = useState<string[]>([])
  const [showLocationModal, setShowLocationModal] = useState(false)
  const [tempLocation, setTempLocation] = useState({
    name: '',
    address: '',
    lat: null as number | null,
    lng: null as number | null,
  })

  const searchBoxRef = useRef<google.maps.places.SearchBox | null>(null)
  const geocoderRef = useRef<google.maps.Geocoder | null>(null)

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || '',
    libraries,
  })

  useEffect(() => {
    if (isLoaded) {
      geocoderRef.current = new window.google.maps.Geocoder()
    }
  }, [isLoaded])

  useEffect(() => {
    const fetchTransportStations = async () => {
      try {
        setStationsLoading(true)
        const response = await fetch(
          `${process.env.REACT_APP_TS_RESTFUL_API_URL}/property/transport/stations`,
        )
        if (!response.ok) {
          throw new Error('Network response was not ok')
        }
        const data = await response.json()
        if (data.result && data.data) {
          const allStations = data.data.all || []
          setBtsmrtStations(allStations)
          setStationsGrouped(data.data.grouped || {})

          const lineNames: string[] = Array.from(
            new Set(allStations.map((station: BTSMRTStation) => station.line_name)),
          )
          setUniqueLines(lineNames)
        } else {
          throw new Error(data.message || 'Failed to fetch transport stations')
        }
      } catch (err: any) {
        setStationsError(err.message || 'An error occurred')
      } finally {
        setStationsLoading(false)
      }
    }

    fetchTransportStations()
  }, [])

  const calculateProgress = () => {
    const fields = [
      formData.location,
      formData.nearbyBTSMRT && formData.nearbyBTSMRT.length > 0,
      formData.nearbyPopularLocations && formData.nearbyPopularLocations.length > 0,
    ]

    const filledFields = fields.filter((field) => field && field !== '').length
    const progressPercentage = Math.round((filledFields / fields.length) * 100)
    return progressPercentage
  }

  useEffect(() => {
    const newProgress = calculateProgress()
    setProgress(newProgress)
    onStepProgress(newProgress)
  }, [formData.location, formData.nearbyBTSMRT, formData.nearbyPopularLocations, onStepProgress])

  useEffect(() => {
    if (showBTSMRTModal && selectedBTSMRTIndex !== null && btsmrtStations.length > 0) {
      const existingBTSMRT = formData.nearbyBTSMRT[selectedBTSMRTIndex]
      const existingStations: BTSMRTStation[] = []

      if (existingBTSMRT.stationId && existingBTSMRT.stationName) {
        const stationIds = existingBTSMRT.stationId.split(',')
        const stationNames = existingBTSMRT.stationName.split(', ')

        stationIds.forEach((id, idx) => {
          const stationName = stationNames[idx]
          if (stationName) {
            const match = stationName.match(/^(.+) \((.+)\)$/)
            if (match) {
              const [, name, lineName] = match
              const foundStation = btsmrtStations.find(
                (station) =>
                  station.id.toString() === id.trim() &&
                  station.name === name.trim() &&
                  station.line_name === lineName.trim(),
              )
              if (foundStation) {
                existingStations.push(foundStation)
              }
            }
          }
        })
      }

      if (existingStations.length > 0) {
        setSelectedStations(existingStations)
      }
    }
  }, [showBTSMRTModal, selectedBTSMRTIndex, btsmrtStations, formData.nearbyBTSMRT])

  const handleInputChange = (field: string, value: any) => {
    updateFormData({ [field]: value })
  }

  const addNearbyBTSMRT = () => {
    const newBTSMRT: NearbyBTSMRT = {
      id: Date.now().toString(),
      stationId: '',
      stationName: '',
      walkingMinutes: '',
    }
    const updatedList = [...(formData.nearbyBTSMRT || []), newBTSMRT]
    updateFormData({ nearbyBTSMRT: updatedList })
  }

  const removeNearbyBTSMRT = (id: string) => {
    const currentList = formData.nearbyBTSMRT || []
    if (currentList.length <= 1) {
      return
    }
    const updatedList = currentList.filter((item) => item.id !== id)
    updateFormData({ nearbyBTSMRT: updatedList })
  }

  const updateNearbyBTSMRT = (id: string, field: string, value: string) => {
    const updatedList = (formData.nearbyBTSMRT || []).map((item) => {
      if (item.id === id) {
        return { ...item, [field]: value }
      }
      return item
    })
    updateFormData({ nearbyBTSMRT: updatedList })
  }

  const openBTSMRTModal = (index: number) => {
    setSelectedBTSMRTIndex(index)
    setShowBTSMRTModal(true)
    setSearchTerm('')
    setSelectedLineFilter('All')

    const existingBTSMRT = formData.nearbyBTSMRT[index]
    const existingStations: BTSMRTStation[] = []

    if (existingBTSMRT.stationId && existingBTSMRT.stationName && btsmrtStations.length > 0) {
      const stationIds = existingBTSMRT.stationId.split(',')
      const stationNames = existingBTSMRT.stationName.split(', ')

      stationIds.forEach((id, idx) => {
        const stationName = stationNames[idx]
        if (stationName) {
          const match = stationName.match(/^(.+) \((.+)\)$/)
          if (match) {
            const [, name, lineName] = match

            const foundStation = btsmrtStations.find(
              (station) =>
                station.id.toString() === id.trim() &&
                station.name === name.trim() &&
                station.line_name === lineName.trim(),
            )
            if (foundStation) {
              existingStations.push(foundStation)
            }
          }
        }
      })
    }

    setSelectedStations(existingStations)
    document.body.style.overflow = 'hidden'
  }

  const toggleStationSelection = (station: BTSMRTStation) => {
    const isSelected = selectedStations.some(
      (s) => s.id === station.id && s.line_name === station.line_name,
    )

    // Single selection: if clicking the same station, deselect it; otherwise select the new one
    let updatedStations: BTSMRTStation[]
    if (isSelected) {
      updatedStations = []
    } else {
      updatedStations = [station]
    }

    setSelectedStations(updatedStations)

    if (selectedBTSMRTIndex !== null) {
      const btsmrtItem = formData.nearbyBTSMRT[selectedBTSMRTIndex]

      if (updatedStations.length > 0) {
        const stationNames = updatedStations.map((s) => `${s.name} (${s.line_name})`).join(', ')
        const stationIds = updatedStations.map((s) => s.id.toString()).join(',')

        const updatedList = (formData.nearbyBTSMRT || []).map((item) => {
          if (item.id === btsmrtItem.id) {
            return { ...item, stationId: stationIds, stationName: stationNames }
          }
          return item
        })
        updateFormData({ nearbyBTSMRT: updatedList })
      } else {
        const updatedList = (formData.nearbyBTSMRT || []).map((item) => {
          if (item.id === btsmrtItem.id) {
            return { ...item, stationId: '', stationName: '' }
          }
          return item
        })
        updateFormData({ nearbyBTSMRT: updatedList })
      }
    }
  }

  const closeBTSMRTModal = () => {
    setShowBTSMRTModal(false)
    setSelectedBTSMRTIndex(null)
    setSelectedStations([])
    document.body.style.overflow = 'unset'
  }

  const getFilteredStations = () => {
    let stationsByLine: { [key: string]: BTSMRTStation[] } = {}

    btsmrtStations.forEach((station) => {
      const lineName = station.line_name
      if (!stationsByLine[lineName]) {
        stationsByLine[lineName] = []
      }
      stationsByLine[lineName].push(station)
    })
    if (selectedLineFilter !== 'All') {
      stationsByLine = {
        [selectedLineFilter]: stationsByLine[selectedLineFilter] || [],
      }
    }
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim()
      Object.keys(stationsByLine).forEach((lineName) => {
        const filteredStations = stationsByLine[lineName].filter(
          (station) =>
            station.name.toLowerCase().includes(searchLower) ||
            station.line_name.toLowerCase().includes(searchLower),
        )

        const uniqueStations = filteredStations.filter((station, index, array) => {
          return (
            index ===
            array.findIndex(
              (s) =>
                s.name.trim().toLowerCase() === station.name.trim().toLowerCase() &&
                s.line_name.trim().toLowerCase() === station.line_name.trim().toLowerCase(),
            )
          )
        })

        stationsByLine[lineName] = uniqueStations

        if (stationsByLine[lineName].length === 0) {
          delete stationsByLine[lineName]
        }
      })
    } else {
      Object.keys(stationsByLine).forEach((lineName) => {
        const uniqueStations = stationsByLine[lineName].filter((station, index, array) => {
          return (
            index ===
            array.findIndex(
              (s) =>
                s.name.trim().toLowerCase() === station.name.trim().toLowerCase() &&
                s.line_name.trim().toLowerCase() === station.line_name.trim().toLowerCase(),
            )
          )
        })
        stationsByLine[lineName] = uniqueStations
      })
    }

    return stationsByLine
  }

  const clearFilters = () => {
    setSearchTerm('')
    setSelectedLineFilter('All')
  }

  const clearAllSelections = () => {
    setSelectedStations([])
    if (selectedBTSMRTIndex !== null) {
      const btsmrtItem = formData.nearbyBTSMRT[selectedBTSMRTIndex]
      const updatedList = (formData.nearbyBTSMRT || []).map((item) => {
        if (item.id === btsmrtItem.id) {
          return { ...item, stationId: '', stationName: '' }
        }
        return item
      })
      updateFormData({ nearbyBTSMRT: updatedList })
    }
  }

  const addNearbyPopularLocation = () => {
    const newLocation: NearbyPopularLocation = {
      id: Date.now().toString(),
      locationName: '',
      walkingMinutes: '',
    }
    const updatedList = [...(formData.nearbyPopularLocations || []), newLocation]
    updateFormData({ nearbyPopularLocations: updatedList })
  }

  const removeNearbyPopularLocation = (id: string) => {
    const currentList = formData.nearbyPopularLocations || []
    if (currentList.length <= 1) {
      return
    }
    const updatedList = currentList.filter((item) => item.id !== id)
    updateFormData({ nearbyPopularLocations: updatedList })
  }

  const updateNearbyPopularLocation = (id: string, field: string, value: string) => {
    const updatedList = (formData.nearbyPopularLocations || []).map((item) => {
      if (item.id === id) {
        return { ...item, [field]: value }
      }
      return item
    })
    updateFormData({ nearbyPopularLocations: updatedList })
  }
  const getLocationNameFromCoordinates = async (lat: number, lng: number): Promise<string> => {
    try {
      // First try Places Nearby Search for actual place names
      if (window.google?.maps?.places?.PlacesService) {
        const service = new window.google.maps.places.PlacesService(document.createElement('div'))

        const request = {
          location: new window.google.maps.LatLng(lat, lng),
          radius: 50,
        }

        return new Promise((resolve) => {
          service.nearbySearch(request, (places, status) => {
            if (
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              places &&
              places.length > 0
            ) {
              let closestPlace = places[0]
              let minDistance = Number.MAX_VALUE

              for (const place of places) {
                if (place.geometry?.location) {
                  const distance = window.google.maps.geometry.spherical.computeDistanceBetween(
                    new window.google.maps.LatLng(lat, lng),
                    place.geometry.location,
                  )
                  if (distance < minDistance) {
                    minDistance = distance
                    closestPlace = place
                  }
                }
              }

              if (minDistance <= 100 && closestPlace.name) {
                resolve(closestPlace.name)
                return
              }
            }

            performGeocodeFallback(lat, lng, resolve)
          })
        })
      } else {
        return new Promise((resolve) => {
          performGeocodeFallback(lat, lng, resolve)
        })
      }
    } catch (error) {
      console.error('Error fetching place name:', error)
      return 'Unknown Location'
    }
  }

  const performGeocodeFallback = (lat: number, lng: number, resolve: (name: string) => void) => {
    if (!window.google?.maps?.Geocoder) {
      resolve('Unknown Location')
      return
    }

    const geocoder = new window.google.maps.Geocoder()
    const location = { lat, lng }

    geocoder.geocode({ location }, (results, status) => {
      if (status === 'OK' && results && results.length > 0) {
        let bestTitle = 'Unknown Location'

        for (const result of results) {
          const isPointOfInterest = result.types.includes('point_of_interest')
          const isEstablishment = result.types.includes('establishment')
          const isPremise = result.types.includes('premise')

          if (isPointOfInterest || isEstablishment || isPremise) {
            const nameComponent = result.address_components?.find(
              (c) =>
                c.types.includes('establishment') ||
                c.types.includes('point_of_interest') ||
                c.types.includes('premise'),
            )

            if (nameComponent) {
              bestTitle = nameComponent.long_name
              break
            }
          }
        }

        if (bestTitle === 'Unknown Location' && results[0].formatted_address) {
          const addressParts = results[0].formatted_address.split(',')
          if (addressParts[0]) {
            const firstPart = addressParts[0].trim()
            if (!firstPart.match(/^[\d+]/)) {
              bestTitle = firstPart
            }
          }
        }

        resolve(bestTitle)
      } else {
        resolve('Unknown Location')
      }
    })
  }

  const reverseGeocodeLocation = async (lat: number, lng: number) => {
    if (!geocoderRef.current || !isLoaded) return

    const location = { lat, lng }
    geocoderRef.current.geocode({ location }, async (results, status) => {
      if (status === 'OK' && results && results.length > 0) {
        const bestResult = results[0]
        let bestAddress = bestResult.formatted_address
        for (const result of results) {
          const isPointOfInterest = result.types.includes('point_of_interest')
          const isEstablishment = result.types.includes('establishment')
          const isPremise = result.types.includes('premise')

          if (isPointOfInterest || isEstablishment || isPremise) {
            bestAddress = result.formatted_address
            break
          }
        }

        // Get place name from coordinates
        const placeName = await getLocationNameFromCoordinates(lat, lng)

        setTempLocation({
          name: placeName,
          address: bestAddress || `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
          lat,
          lng,
        })
      } else {
        const placeName = await getLocationNameFromCoordinates(lat, lng)
        setTempLocation({
          name: placeName,
          address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
          lat,
          lng,
        })
      }
    })
  }

  const openLocationModal = () => {
    setTempLocation({
      name: formData.locationName || '',
      address: formData.location || '',
      lat: formData.locationLat,
      lng: formData.locationLng,
    })
    setShowLocationModal(true)
    document.body.style.overflow = 'hidden'
  }

  const closeLocationModal = () => {
    setShowLocationModal(false)
    document.body.style.overflow = 'unset'
  }

  const confirmLocationSelection = () => {
    handleInputChange('locationName', tempLocation.name)
    handleInputChange('location', tempLocation.address)
    updateFormData({
      locationLat: tempLocation.lat,
      locationLng: tempLocation.lng,
    })
    closeLocationModal()
  }

  const isNextDisabled = progress < 100

  return (
    <div
      style={{
        backgroundColor: '#fff',
        minHeight: '100vh',
        fontFamily: 'Poppins, sans-serif',
      }}
    >
      <div style={{ padding: '24px 16px' }}>
        <div style={{ marginBottom: '24px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '8px',
              fontWeight: '400',
            }}
          >
            Location
          </label>
          <div style={{ position: 'relative', width: '100%' }}>
            <input
              type="text"
              placeholder="Select location from map"
              value={formData.locationName || formData.location || ''}
              onClick={openLocationModal}
              readOnly
              style={{
                width: '100%',
                padding: '12px 40px 12px 12px',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'Poppins, sans-serif',
                boxSizing: 'border-box',
                cursor: 'pointer',
              }}
            />
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
              }}
            >
              <path
                d="M9 18l6-6-6-6"
                stroke="#666"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        <div style={{ marginBottom: '32px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '12px',
              fontWeight: '400',
            }}
          >
            Nearby BTS/MRT
          </label>

          {(formData.nearbyBTSMRT || []).map((item, index) => (
            <div key={item.id} style={{ marginBottom: '24px' }}>
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  marginBottom: '12px',
                  alignItems: 'center',
                }}
              >
                <button
                  onClick={() => openBTSMRTModal(index)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Poppins, sans-serif',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    textAlign: 'left',
                    color: item.stationName ? '#333' : '#999',
                  }}
                >
                  {item.stationName || 'Select nearby stations'}
                </button>
                {index > 0 && (
                  <button
                    onClick={() => removeNearbyBTSMRT(item.id)}
                    style={{
                      width: '40px',
                      height: '40px',
                      border: 'none',
                      borderRadius: '8px',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/48viVYX34QktwUER4CUVhAGXLaGyifB6d5kTyehTJwhodTCphp2JbtDP9jueDkGwhU4J4ZmgQuayswASXuVVCwcA.png"
                      alt="Delete"
                      style={{ width: '20px', height: '20px' }}
                    />
                  </button>
                )}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    color: '#333',
                    marginBottom: '8px',
                    fontWeight: '400',
                  }}
                >
                  Walking minutes to nearest BTS/MRT
                </label>
                <div style={{ position: 'relative', width: '100%' }}>
                  <input
                    type="number"
                    placeholder="Enter walking minutes"
                    value={item.walkingMinutes}
                    onChange={(e) => updateNearbyBTSMRT(item.id, 'walkingMinutes', e.target.value)}
                    onWheel={(e) => e.currentTarget.blur()}
                    onKeyDown={(e) => {
                      if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                        e.preventDefault()
                      }
                    }}
                    className="number-input-no-spinner"
                    style={{
                      width: '100%',
                      padding: '12px 35px 12px 12px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'Poppins, sans-serif',
                      boxSizing: 'border-box',
                    }}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: '14px',
                      color: '#999',
                      fontFamily: 'Poppins, sans-serif',
                    }}
                  >
                    mins
                  </span>
                </div>
              </div>
            </div>
          ))}

          <span
            onClick={addNearbyBTSMRT}
            style={{
              fontSize: '14px',
              color: '#4C4848',
              fontFamily: 'Poppins, sans-serif',
              cursor: 'pointer',
              textDecoration: 'underline',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginTop: '12px',
            }}
          >
            <img
              src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/4YLP6dJPXYM2osUYAFXnW2wJHYZwvzCsQfMnSx2phyke1DofNEmEJT1cEMzcJMvEdbwPwSLKspQ3H86WUnKPjnRZ.png"
              alt="Add"
              style={{ width: '16px', height: '16px' }}
            />
            Add more nearby BTS/MRT
          </span>
        </div>

        <div style={{ marginBottom: '32px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '8px',
              fontWeight: '400',
            }}
          >
            Nearby popular locations (optional)
          </label>

          {(formData.nearbyPopularLocations || []).map((item, index) => (
            <div key={item.id} style={{ marginBottom: '24px' }}>
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  marginBottom: '12px',
                  alignItems: 'center',
                }}
              >
                <input
                  type="text"
                  placeholder="Enter nearby popular locations"
                  value={item.locationName}
                  onChange={(e) =>
                    updateNearbyPopularLocation(item.id, 'locationName', e.target.value)
                  }
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Poppins, sans-serif',
                    boxSizing: 'border-box',
                  }}
                />
                {index > 0 && (
                  <button
                    onClick={() => removeNearbyPopularLocation(item.id)}
                    style={{
                      width: '40px',
                      height: '40px',
                      border: 'none',
                      borderRadius: '8px',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/48viVYX34QktwUER4CUVhAGXLaGyifB6d5kTyehTJwhodTCphp2JbtDP9jueDkGwhU4J4ZmgQuayswASXuVVCwcA.png"
                      alt="Delete"
                      style={{ width: '20px', height: '20px' }}
                    />
                  </button>
                )}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    color: '#333',
                    marginBottom: '8px',
                    fontWeight: '400',
                  }}
                >
                  Walking mins
                </label>
                <div style={{ position: 'relative', width: '100%' }}>
                  <input
                    type="number"
                    placeholder="Enter walking minutes"
                    value={item.walkingMinutes}
                    onChange={(e) =>
                      updateNearbyPopularLocation(item.id, 'walkingMinutes', e.target.value)
                    }
                    onWheel={(e) => e.currentTarget.blur()}
                    onKeyDown={(e) => {
                      if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                        e.preventDefault()
                      }
                    }}
                    className="number-input-no-spinner"
                    style={{
                      width: '100%',
                      padding: '12px 35px 12px 12px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'Poppins, sans-serif',
                      boxSizing: 'border-box',
                    }}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: '14px',
                      color: '#999',
                      fontFamily: 'Poppins, sans-serif',
                    }}
                  >
                    mins
                  </span>
                </div>
              </div>
            </div>
          ))}

          <span
            onClick={addNearbyPopularLocation}
            style={{
              fontSize: '14px',
              color: '#4C4848',
              fontFamily: 'Poppins, sans-serif',
              cursor: 'pointer',
              textDecoration: 'underline',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginTop: '12px',
            }}
          >
            <img
              src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/4YLP6dJPXYM2osUYAFXnW2wJHYZwvzCsQfMnSx2phyke1DofNEmEJT1cEMzcJMvEdbwPwSLKspQ3H86WUnKPjnRZ.png"
              alt="Add"
              style={{ width: '16px', height: '16px' }}
            />
            Add more nearby popular location
          </span>
        </div>

        <div
          style={{
            display: 'flex',
            gap: '12px',
            marginTop: '32px',
            paddingBottom: '32px',
          }}
        >
          <button
            onClick={onPrevious}
            style={{
              flex: '1',
              padding: '12px 24px',
              backgroundColor: 'transparent',
              color: '#666',
              border: '1px solid #e0e0e0',
              borderRadius: '24px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '16px',
              fontFamily: 'Poppins, sans-serif',
              transition: 'all 0.2s ease',
              transform: 'scale(1)',
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'scale(0.95)'
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
            }}
          >
            Back
          </button>
          <button
            onClick={onNext}
            disabled={isNextDisabled}
            style={{
              flex: '1',
              padding: '12px 24px',
              backgroundColor: isNextDisabled ? '#ccc' : '#4C4848',
              color: 'white',
              border: 'none',
              borderRadius: '24px',
              cursor: isNextDisabled ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              fontSize: '16px',
              fontFamily: 'Poppins, sans-serif',
              opacity: isNextDisabled ? 0.6 : 1,
              transition: 'all 0.2s ease',
              transform: 'scale(1)',
              boxShadow: !isNextDisabled ? '0 2px 8px rgba(76, 72, 72, 0.3)' : 'none',
            }}
            onMouseDown={(e) => {
              if (!isNextDisabled) {
                e.currentTarget.style.transform = 'scale(0.95)'
              }
            }}
            onMouseUp={(e) => {
              if (!isNextDisabled) {
                e.currentTarget.style.transform = 'scale(1)'
              }
            }}
            onMouseLeave={(e) => {
              if (!isNextDisabled) {
                e.currentTarget.style.transform = 'scale(1)'
              }
            }}
          >
            Next
          </button>
        </div>
      </div>

      {showBTSMRTModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={closeBTSMRTModal}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '490px',
              width: '90%',
              maxHeight: '85vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px',
              }}
            >
              <h3
                style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  margin: 0,
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                Select BTS/MRT Station
              </h3>
              <button
                onClick={closeBTSMRTModal}
                style={{
                  width: '32px',
                  height: '32px',
                  border: 'none',
                  borderRadius: '50%',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  transition: 'background-color 0.2s ease',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5'
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#666"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            {/* Search Input */}
            <div style={{ marginBottom: '16px' }}>
              <input
                type="text"
                placeholder="Search stations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Poppins, sans-serif',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Line Filter Buttons */}
            <div style={{ marginBottom: '16px' }}>
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  overflowX: 'auto',
                  paddingBottom: '8px',
                  marginBottom: '8px',
                }}
              >
                {['All', ...uniqueLines].map((filterType) => (
                  <button
                    key={filterType}
                    onClick={() => setSelectedLineFilter(filterType)}
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '16px',
                      backgroundColor: selectedLineFilter === filterType ? '#4C4848' : '#fff',
                      color: selectedLineFilter === filterType ? '#fff' : '#666',
                      cursor: 'pointer',
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: '500',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {filterType}
                  </button>
                ))}
              </div>
              {selectedStations.length > 0 && (
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span
                    style={{
                      fontSize: '12px',
                      color: '#333',
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: '500',
                    }}
                  >
                    Selected: {selectedStations[0].name} ({selectedStations[0].line_name})
                  </span>
                  <span
                    onClick={clearAllSelections}
                    style={{
                      fontSize: '12px',
                      color: '#ef4444',
                      cursor: 'pointer',
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: '500',
                      textDecoration: 'underline',
                      transition: 'color 0.2s ease',
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.color = '#dc2626'
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.color = '#ef4444'
                    }}
                  >
                    Clear
                  </span>
                </div>
              )}
            </div>

            {stationsLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                Loading stations...
              </div>
            ) : stationsError ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>
                Error: {stationsError}
              </div>
            ) : (
              <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                {(() => {
                  const filteredStations = getFilteredStations()
                  const hasResults = Object.keys(filteredStations).length > 0

                  if (!hasResults) {
                    return (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        No stations found matching your criteria.
                      </div>
                    )
                  }

                  return Object.entries(filteredStations).map(([lineName, stations]) => (
                    <div key={lineName} style={{ marginBottom: '24px' }}>
                      <h4
                        style={{
                          fontSize: '16px',
                          fontWeight: '500',
                          marginBottom: '12px',
                          fontFamily: 'Poppins, sans-serif',
                          color: '#4C4848',
                          borderBottom: '1px solid #e0e0e0',
                          paddingBottom: '8px',
                        }}
                      >
                        {lineName}
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {stations.map((station) => {
                          const isSelected = selectedStations.some(
                            (s) => s.id === station.id && s.line_name === station.line_name,
                          )
                          return (
                            <button
                              key={`${station.id}-${station.type}-${station.line_name}`}
                              onClick={() => toggleStationSelection(station)}
                              style={{
                                padding: '12px',
                                border: `1px solid ${isSelected ? '#4C4848' : '#e0e0e0'}`,
                                borderRadius: '8px',
                                backgroundColor: isSelected ? '#f8f5f3' : '#fff',
                                cursor: 'pointer',
                                textAlign: 'left',
                                fontSize: '14px',
                                fontFamily: 'Poppins, sans-serif',
                                transition: 'all 0.2s ease',
                                position: 'relative',
                              }}
                              onMouseOver={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.backgroundColor = '#f5f5f5'
                                }
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = isSelected
                                  ? '#f8f5f3'
                                  : '#fff'
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: '500',
                                  color: isSelected ? '#4C4848' : '#333',
                                }}
                              >
                                {station.name}
                              </div>
                              <div style={{ fontSize: '12px', color: '#666' }}>{station.type}</div>
                              {isSelected && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    right: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '50%',
                                    backgroundColor: '#4C4848',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                    <path
                                      d="M20 6L9 17l-5-5"
                                      stroke="white"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </div>
                              )}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ))
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      {showLocationModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
            paddingTop: '40px',
          }}
          onClick={closeLocationModal}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '490px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px',
              }}
            >
              <h3
                style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  margin: 0,
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                Select Location on Map
              </h3>
              <button
                onClick={closeLocationModal}
                style={{
                  width: '32px',
                  height: '32px',
                  border: 'none',
                  borderRadius: '50%',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  transition: 'background-color 0.2s ease',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5'
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#666"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            {isLoaded && window.google?.maps ? (
              <>
                <StandaloneSearchBox
                  onLoad={(ref) => (searchBoxRef.current = ref)}
                  onPlacesChanged={async () => {
                    const places = searchBoxRef.current?.getPlaces()
                    const place = places?.[0]
                    if (place?.geometry?.location) {
                      const lat = place.geometry.location.lat()
                      const lng = place.geometry.location.lng()
                      const placeName =
                        place.name || (await getLocationNameFromCoordinates(lat, lng))
                      setTempLocation({
                        name: placeName,
                        address: place.formatted_address || place.name || '',
                        lat,
                        lng,
                      })
                    }
                  }}
                >
                  <input
                    type="text"
                    placeholder="Search for a location..."
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'Poppins, sans-serif',
                      boxSizing: 'border-box',
                      marginBottom: '16px',
                    }}
                  />
                </StandaloneSearchBox>

                <div
                  style={{
                    position: 'relative',
                    height: '350px',
                    width: '100%',
                    marginBottom: '16px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}
                >
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={{
                      lat: tempLocation.lat || formData.locationLat || 13.72,
                      lng: tempLocation.lng || formData.locationLng || 100.52,
                    }}
                    zoom={tempLocation.lat && tempLocation.lng ? 15 : 12}
                    options={{
                      fullscreenControl: false,
                      zoomControl: true,
                      streetViewControl: false,
                      mapTypeControl: false,
                    }}
                    onClick={(e) => {
                      const lat = e.latLng?.lat()
                      const lng = e.latLng?.lng()
                      if (lat && lng) {
                        reverseGeocodeLocation(lat, lng)
                      }
                    }}
                  >
                    <Marker
                      position={{
                        lat: tempLocation.lat || formData.locationLat || 13.72,
                        lng: tempLocation.lng || formData.locationLng || 100.52,
                      }}
                      draggable={true}
                      onDragEnd={(e) => {
                        const lat = e.latLng?.lat()
                        const lng = e.latLng?.lng()
                        if (lat && lng) {
                          reverseGeocodeLocation(lat, lng)
                        }
                      }}
                    />
                  </GoogleMap>
                </div>

                {tempLocation.address && (
                  <div style={{ marginBottom: '16px' }}>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        color: '#333',
                        marginBottom: '8px',
                        fontWeight: '400',
                      }}
                    >
                      Place Name
                    </label>
                    <input
                      type="text"
                      value={tempLocation.name}
                      onChange={(e) => setTempLocation({ ...tempLocation, name: e.target.value })}
                      placeholder="Enter place name"
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontFamily: 'Poppins, sans-serif',
                        boxSizing: 'border-box',
                        marginBottom: '12px',
                      }}
                    />
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        color: '#333',
                        marginBottom: '8px',
                        fontWeight: '400',
                      }}
                    >
                      Address
                    </label>
                    <div
                      style={{
                        backgroundColor: '#f8f9fa',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0',
                        fontSize: '14px',
                        color: '#666',
                        fontFamily: 'Poppins, sans-serif',
                      }}
                    >
                      {tempLocation.address}
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={confirmLocationSelection}
                    disabled={!tempLocation.address}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: tempLocation.address ? '#4C4848' : '#ccc',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: tempLocation.address ? 'pointer' : 'not-allowed',
                      fontSize: '14px',
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: '500',
                    }}
                  >
                    Confirm Location
                  </button>
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                Loading Map...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default Step1_1
